import { getCheckSessionQueryKey, useCheckSession } from '@/api/check-session.ts'
import { useCreateSession } from '@/api/create-session.ts'
import {
  GetUserDetailsType,
  getUserDetailsQueryKey,
  getUserDetailsQueryOptions,
} from '@/api/get-user-details.ts'
import { useMakeLogout } from '@/api/logout.ts'
import { AppAbility } from '@/app/permissions'
import defineAbilityForUser from '@/lib/casl.ts'
import { eventsManager } from '@/utils/events-manager.ts'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { PropsWithChildren, createContext, useCallback, useMemo } from 'react'

export interface AuthContextProps {
  isAuthenticated: boolean
  logout: () => Promise<void>
  login: (email: string, password: string) => Promise<void>
  permissions: AppAbility
  isPending: boolean
  user: GetUserDetailsType | null
}

export const AuthContext = createContext({} as AuthContextProps)

const queryKeyList = [getUserDetailsQueryKey(), getCheckSessionQueryKey()]

export function AuthProvider({ children }: PropsWithChildren) {
  const queryClient = useQueryClient()
  const { data: isHaveRefreshToken } = useCheckSession()

  const { data: user, isLoading } = useQuery({
    ...getUserDetailsQueryOptions(),
    enabled: false,
  })

  const logoutMutation = useMakeLogout()
  const createSessionMutation = useCreateSession()

  const logout = useCallback(async () => {
    await logoutMutation.mutateAsync({ refreshToken: null })
    eventsManager.dispatch('auth:unauthenticated')
  }, [])

  const login = useCallback(async (email: string, password: string) => {
    await createSessionMutation.mutateAsync({ email, password })

    await queryClient.invalidateQueries({
      predicate: (query) => {
        return queryKeyList.some((key) => key === query.queryKey)
      },
    })
  }, [])

  const ability = useMemo(
    () =>
      defineAbilityForUser({
        isTelegramSubscriber: !!user?.isTelegramSubscriber,
        trialQuoteLimit: user?.trialQuoteLimit ?? null,
        notionDatabaseId: user?.notionDatabaseId,
        role: user?.role || 'USER',
        id: user?.id || '',
        emailConfirmed: user?.emailConfirmed ?? false,
      }),
    [user],
  )

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: !!isHaveRefreshToken,
        logout,
        login,
        permissions: ability,
        isPending: isLoading,
        user: user || null,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}
